import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import {
  InventoryService,
  InventoryCreateEntry,
} from '../services/inventoryService';
import {
  Inventory,
  InventoryCategory,
  Warehouse,
  Provider,
  Ingredient,
  WEIGHT_UNITS,
  VOLUME_UNITS,
  DEFAULT_WEIGHT_UNIT,
  DEFAULT_VOLUME_UNIT,
  getWeightUnitKeys,
  getVolumeUnitKeys,
  type WeightUnit,
  type VolumeUnit,
} from 'public-contracts';
import { ingredientsService } from '../services/ingredientsService';
import { ModernIngredientForm } from '../ingredients/ModernIngredientForm';
import { inventoryQRPrintService } from './services/inventoryQRPrintService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { IngredientCombobox } from '../components/IngredientCombobox';
import { ProductCombobox } from '../components/ProductCombobox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

// Lucide React icons
import {
  Package,
  Save,
  X,
  AlertTriangle,
  Warehouse as WarehouseIcon,
  Tag,
  DollarSign,
  Hash,
  Ruler,
  Weight,
  Box,
  Printer,
  Plus,
} from 'lucide-react';

interface InventoryFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (inventory: Inventory) => void;
  inventory?: Inventory | null;
}

export const ModernInventoryForm: React.FC<InventoryFormProps> = ({
  open,
  onClose,
  onSave,
  inventory,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [ingredientFormOpen, setIngredientFormOpen] = useState(false);

  const [formData, setFormData] = useState<InventoryCreateEntry>({
    code: '',
    name: '',
    description: '',
    quantity: 0,
    totalPrice: 0,
    currency: 'VND',
    warehouseId: '',
    inventoryCategoryId: '',
    providerId: '',
    width: 0,
    height: 0,
    weight: 0,
    weightUnit: DEFAULT_WEIGHT_UNIT,
    volume: 0,
    volumeUnit: DEFAULT_VOLUME_UNIT,
    isPackage: false,
    itemCount: undefined,
    quantityAnchor: undefined,
    remainingItems: undefined,
    remainingWeight: undefined,
    remainingVolume: undefined,
  });

  useEffect(() => {
    if (open) {
      loadWarehouses();
      loadCategories();
      loadProviders();
      loadIngredients();
      if (inventory) {
        setFormData({
          code: inventory.code,
          name: inventory.name,
          description: inventory.description,
          quantity: inventory.quantity,
          totalPrice: inventory.totalPrice,
          currency: inventory.currency,
          warehouseId: inventory.warehouseId,
          inventoryCategoryId: inventory.inventoryCategoryId,
          providerId: inventory.providerId || '',
          ingredientId: inventory.ingredientId || '',
          productId: inventory.productId || '',
          width: inventory.width || 0,
          height: inventory.height || 0,
          weight: inventory.weight || 0,
          weightUnit:
            (inventory.weightUnit as WeightUnit) || DEFAULT_WEIGHT_UNIT,
          volume: inventory.volume || 0,
          volumeUnit:
            (inventory.volumeUnit as VolumeUnit) || DEFAULT_VOLUME_UNIT,
          isPackage: inventory.isPackage || false,
          itemCount: inventory.isPackage ? inventory.itemCount : undefined,
          quantityAnchor: inventory.quantityAnchor,
          remainingItems: inventory.remainingItems,
          remainingWeight: inventory.remainingWeight,
          remainingVolume: inventory.remainingVolume,
        });
      } else {
        // Initialize empty form for new inventory
        // Code will be auto-generated when category is selected
        setFormData({
          code: '',
          name: '',
          description: '',
          quantity: 0,
          totalPrice: 0,
          currency: 'VND',
          warehouseId: '',
          inventoryCategoryId: '',
          providerId: '',
          width: 0,
          height: 0,
          weight: 0,
          weightUnit: DEFAULT_WEIGHT_UNIT,
          volume: 0,
          volumeUnit: DEFAULT_VOLUME_UNIT,
          isPackage: false,
          itemCount: undefined,
          quantityAnchor: undefined,
          remainingItems: undefined,
          remainingWeight: undefined,
          remainingVolume: undefined,
        });
      }
    }
  }, [open, inventory]);

  useEffect(() => {
    if (warehouses && warehouses.length > 0 && !formData.warehouseId) {
      const defaultWarehouseId = warehouses[0]?.id;
      if (defaultWarehouseId) {
        console.log('✅ Setting default warehouse:', defaultWarehouseId);
        setFormData((prev) => ({ ...prev, warehouseId: defaultWarehouseId }));
      }
    }
  }, [warehouses, formData.warehouseId]);

  useEffect(() => {
    if (categories && categories.length > 0 && !formData.inventoryCategoryId) {
      const defaultCategoryId = categories[0]?.id;
      if (defaultCategoryId) {
        console.log('✅ Setting default category:', defaultCategoryId);
        setFormData((prev) => ({
          ...prev,
          inventoryCategoryId: defaultCategoryId,
        }));
      }
    }
  }, [categories, formData.inventoryCategoryId]);

  // Auto-generate code when category is selected (only for new inventory)
  useEffect(() => {
    const generateCode = async () => {
      if (!inventory && formData.inventoryCategoryId) {
        try {
          const generatedCode = await InventoryService.generateNextCode(
            formData.inventoryCategoryId
          );
          setFormData((prev) => ({ ...prev, code: generatedCode }));
        } catch (error) {
          console.error('Failed to generate code:', error);
          // Fallback to timestamp-based code
          const timestamp = Date.now().toString().slice(-8);
          setFormData((prev) => ({ ...prev, code: `INV-${timestamp}` }));
        }
      }
    };

    generateCode();
  }, [formData.inventoryCategoryId, inventory]);

  const loadWarehouses = async () => {
    try {
      const warehouses = await InventoryService.getWarehouses();
      console.log('✅ Loaded warehouses:', warehouses.length);
      setWarehouses(warehouses);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const categories = await InventoryService.getInventoryCategories();
      console.log('✅ Loaded categories:', categories.length);
      setCategories(categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadProviders = async () => {
    try {
      const providers = await InventoryService.getProviders();
      console.log('✅ Loaded providers:', providers.length);
      setProviders(providers);
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  };

  const loadIngredients = async () => {
    try {
      // Reload ingredients - IngredientCombobox will handle displaying them
      await ingredientsService.getIngredients({
        page: 1,
        size: 1000,
      });
    } catch (err) {
      console.error('Failed to load ingredients:', err);
    }
  };

  const handlePrintQRCode = async () => {
    if (!inventory) return;

    try {
      await inventoryQRPrintService.printQRCode(
        inventory.id,
        inventory.code,
        inventory.name,
        {
          size: 200,
          includeLabel: true,
          includeCode: true,
          includeName: true,
          labelText: 'Inventory QR Code',
        }
      );
    } catch (error) {
      console.error('Failed to print QR code:', error);
      // You could add a toast notification here
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let savedInventory: Inventory;

      if (inventory) {
        savedInventory = await InventoryService.updateInventory(
          inventory.id,
          formData
        );
      } else {
        // For new inventory, ensure code is generated if not already set
        const submitData = { ...formData };
        if (!submitData.code && submitData.inventoryCategoryId) {
          try {
            submitData.code = await InventoryService.generateNextCode(
              submitData.inventoryCategoryId
            );
          } catch (error) {
            console.error('Failed to generate code:', error);
            // Fallback to timestamp-based code
            const timestamp = Date.now().toString().slice(-8);
            submitData.code = `INV-${timestamp}`;
          }
        }
        savedInventory = await InventoryService.createInventory(submitData);
      }

      // ingredientId and productId are now part of formData and saved directly with inventory

      console.log('✅ Inventory saved, calling onSave callback');
      onSave(savedInventory);
      console.log('✅ Calling onClose callback');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof InventoryCreateEntry, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    const basicValid =
      formData.name &&
      formData.description &&
      formData.quantity >= 0 &&
      formData.totalPrice >= 0 &&
      formData.warehouseId &&
      formData.inventoryCategoryId &&
      // Code is required when editing, but auto-generated when creating
      (inventory ? formData.code : true);

    // For packages with anchor set, validate remaining values
    if (formData.isPackage && formData.quantityAnchor) {
      if (formData.quantityAnchor === 'ITEM_COUNT') {
        return (
          basicValid &&
          formData.remainingItems !== undefined &&
          formData.remainingItems >= 0
        );
      }
      if (formData.quantityAnchor === 'WEIGHT') {
        return (
          basicValid &&
          formData.remainingWeight !== undefined &&
          formData.remainingWeight >= 0
        );
      }
      if (formData.quantityAnchor === 'VOLUME') {
        return (
          basicValid &&
          formData.remainingVolume !== undefined &&
          formData.remainingVolume >= 0
        );
      }
    }

    return basicValid;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>
              {inventory
                ? translate('pos.inventory.edit', 'Edit Inventory')
                : translate('pos.inventory.create', 'Create Inventory')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {inventory
              ? translate(
                  'pos.inventory.editDescription',
                  'Update inventory item details'
                )
              : translate(
                  'pos.inventory.createDescription',
                  'Add a new inventory item to the system'
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate('pos.inventory.basicInfo', 'Basic Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">
                    {translate('pos.inventory.fields.code', 'Code')} *
                    {!inventory && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (Auto-generated)
                      </span>
                    )}
                  </Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="code"
                      placeholder={translate(
                        'pos.inventory.fields.codePlaceholder',
                        'Enter inventory code'
                      )}
                      value={formData.code}
                      onChange={(e) =>
                        handleInputChange('code', e.target.value)
                      }
                      className="pl-10"
                      disabled={!inventory} // Disable when creating, allow edit when updating
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {translate('pos.inventory.fields.name', 'Name')} *
                  </Label>
                  <Input
                    id="name"
                    placeholder={translate(
                      'pos.inventory.fields.namePlaceholder',
                      'Enter inventory name'
                    )}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.inventory.fields.description', 'Description')}{' '}
                  *
                </Label>
                <Textarea
                  id="description"
                  placeholder={translate(
                    'pos.inventory.fields.descriptionPlaceholder',
                    'Enter inventory description'
                  )}
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="warehouse">
                    {translate('pos.inventory.warehouse.name', 'Warehouse')} *
                  </Label>
                  <Select
                    value={formData.warehouseId}
                    onValueChange={(value) =>
                      handleInputChange('warehouseId', value)
                    }
                  >
                    <SelectTrigger data-testid="warehouse-select">
                      <SelectValue
                        placeholder={translate(
                          'pos.inventory.warehouse.placeholder',
                          'Select warehouse'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses && warehouses.length > 0 ? (
                        warehouses.map((warehouse) => (
                          <SelectItem key={warehouse.id} value={warehouse.id}>
                            <div className="flex items-center space-x-2">
                              <WarehouseIcon className="h-4 w-4" />
                              <span>{warehouse.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          {translate('pos.inventory.noWarehouses', 'No warehouses available')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">
                    {translate('pos.inventory.category.name', 'Category')} *
                  </Label>
                  <Select
                    value={formData.inventoryCategoryId}
                    onValueChange={(value) =>
                      handleInputChange('inventoryCategoryId', value)
                    }
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue
                        placeholder={translate(
                          'pos.inventory.category.placeholder',
                          'Select category'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {categories && categories.length > 0 ? (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <div className="flex items-center space-x-2">
                              <Tag className="h-4 w-4" />
                              <span>{category.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          {translate('pos.inventory.noCategories', 'No categories available')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider">
                  {translate('pos.inventory.provider.name', 'Provider')}
                </Label>
                <Select
                  value={formData.providerId || 'none'}
                  onValueChange={(value) =>
                    handleInputChange(
                      'providerId',
                      value === 'none' ? undefined : value
                    )
                  }
                >
                  <SelectTrigger data-testid="provider-select">
                    <SelectValue
                      placeholder={translate(
                        'pos.inventory.provider.placeholder',
                        'Select provider (optional)'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">
                        {translate(
                          'pos.inventory.provider.none',
                          'No provider'
                        )}
                      </span>
                    </SelectItem>
                    {providers && providers.length > 0 &&
                      providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          <div className="flex items-center space-x-2">
                            <Package className="h-4 w-4" />
                            <span>{provider.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ingredient">
                  {translate('pos.inventory.ingredient.name', 'Ingredient')}
                </Label>
                <div className="flex gap-2">
                  <IngredientCombobox
                    value={formData.ingredientId}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        ingredientId: value,
                        productId: '', // Clear productId when ingredient is selected
                      }))
                    }
                    placeholder={translate(
                      'pos.inventory.ingredient.placeholder',
                      'Select ingredient (optional)'
                    )}
                    allowNone={true}
                    noneLabel={translate(
                      'pos.inventory.ingredient.none',
                      'No ingredient'
                    )}
                    className="flex-1"
                    data-testid="ingredient-select"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setIngredientFormOpen(true)}
                    title={translate(
                      'pos.inventory.ingredient.quickCreate',
                      'Quick create ingredient'
                    )}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="product">
                  {translate('pos.inventory.product.name', 'Product')}
                </Label>
                <ProductCombobox
                  value={formData.productId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      productId: value,
                      ingredientId: '', // Clear ingredientId when product is selected
                    }))
                  }
                  placeholder={translate(
                    'pos.inventory.product.placeholder',
                    'Select product (optional)'
                  )}
                  allowNone={true}
                  noneLabel={translate(
                    'pos.inventory.product.none',
                    'No product'
                  )}
                  className="w-full"
                  data-testid="product-select"
                />
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.inventory.product.help',
                    'Select a product for basic items (e.g., Fanta, Surprise, Pepsi). Leave empty if linking to an ingredient.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quantity and Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate(
                  'pos.inventory.quantityPricing',
                  'Quantity & Pricing'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">
                    {translate('pos.inventory.fields.quantity', 'Quantity')} *
                    {formData.isPackage && formData.quantityAnchor && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (
                        {translate(
                          'pos.inventory.fields.calculated',
                          'Calculated'
                        )}
                        )
                      </span>
                    )}
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="0"
                    value={formData.quantity}
                    onChange={(e) =>
                      handleInputChange(
                        'quantity',
                        parseFloat(e.target.value) || 0
                      )
                    }
                    disabled={formData.isPackage && !!formData.quantityAnchor}
                    required
                  />
                  {formData.isPackage === true &&
                    formData.itemCount !== undefined &&
                    formData.itemCount !== null &&
                    formData.itemCount > 0 &&
                    !formData.quantityAnchor && (
                      <p className="text-xs text-muted-foreground">
                        {translate(
                          'pos.inventory.fields.quantityHelp',
                          `Total items: ${(
                            formData.quantity * formData.itemCount
                          ).toFixed(1)} (${formData.quantity} packages × ${
                            formData.itemCount
                          } items)`,
                          {
                            total: (
                              formData.quantity * formData.itemCount
                            ).toFixed(1),
                            quantity: formData.quantity,
                            itemCount: formData.itemCount,
                          }
                        )}
                      </p>
                    )}
                  {formData.isPackage && formData.quantityAnchor && (
                    <p className="text-xs text-muted-foreground">
                      {translate(
                        'pos.inventory.fields.quantityCalculatedHelp',
                        'Quantity is automatically calculated from the anchor property'
                      )}
                    </p>
                  )}
                  {formData.isPackage &&
                    formData.quantityAnchor === 'ITEM_COUNT' &&
                    formData.itemCount &&
                    formData.itemCount > 0 &&
                    (formData.remainingItems !== undefined ||
                      inventory?.remainingItems !== undefined) && (
                      <p className="text-xs text-muted-foreground font-medium">
                        {translate(
                          'pos.inventory.fields.currentStock',
                          `Current stock: ${
                            formData.remainingItems ??
                            inventory?.remainingItems ??
                            0
                          } items (${
                            formData.quantity ?? inventory?.quantity ?? 0
                          } packages × ${
                            formData.itemCount
                          } items per package)`,
                          {
                            total:
                              formData.remainingItems ??
                              inventory?.remainingItems ??
                              0,
                            quantity:
                              formData.quantity ?? inventory?.quantity ?? 0,
                            itemCount: formData.itemCount,
                          }
                        )}
                      </p>
                    )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalPrice">
                    {translate(
                      'pos.inventory.fields.totalPrice',
                      'Total Price'
                    )}{' '}
                    *
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="totalPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.totalPrice}
                      onChange={(e) =>
                        handleInputChange(
                          'totalPrice',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">
                    {translate('pos.inventory.fields.currency', 'Currency')} *
                  </Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) =>
                      handleInputChange('currency', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VND">VND</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Package Dimensions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate('pos.inventory.dimensions', 'Package Dimensions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isPackage"
                  checked={formData.isPackage}
                  onCheckedChange={(checked) => {
                    handleInputChange('isPackage', checked);
                    // Clear itemCount and anchor-related fields when isPackage is set to false
                    if (!checked) {
                      handleInputChange('itemCount', undefined);
                      handleInputChange('quantityAnchor', undefined);
                      handleInputChange('remainingItems', undefined);
                      handleInputChange('remainingWeight', undefined);
                      handleInputChange('remainingVolume', undefined);
                    }
                  }}
                />
                <Label htmlFor="isPackage">
                  {translate(
                    'pos.inventory.fields.isPackage',
                    'This is a package item'
                  )}
                </Label>
              </div>

              {formData.isPackage && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="width">
                      {translate('pos.inventory.fields.width', 'Width (cm)')}
                    </Label>
                    <div className="relative">
                      <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="width"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0.0"
                        value={formData.width}
                        onChange={(e) =>
                          handleInputChange(
                            'width',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height">
                      {translate('pos.inventory.fields.height', 'Height (cm)')}
                    </Label>
                    <div className="relative">
                      <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="height"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0.0"
                        value={formData.height}
                        onChange={(e) =>
                          handleInputChange(
                            'height',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight">
                      {translate('pos.inventory.fields.weight', 'Weight')}
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Weight className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="weight"
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0.0"
                          value={formData.weight}
                          onChange={(e) =>
                            handleInputChange(
                              'weight',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="pl-10"
                        />
                      </div>
                      <Select
                        value={formData.weightUnit || DEFAULT_WEIGHT_UNIT}
                        onValueChange={(value) =>
                          handleInputChange('weightUnit', value as WeightUnit)
                        }
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getWeightUnitKeys().map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {WEIGHT_UNITS[unit].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="volume">
                      {translate('pos.inventory.fields.volume', 'Volume')}
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Box className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="volume"
                          type="number"
                          min="0"
                          step="0.001"
                          placeholder="0.000"
                          value={formData.volume}
                          onChange={(e) =>
                            handleInputChange(
                              'volume',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="pl-10"
                        />
                      </div>
                      <Select
                        value={formData.volumeUnit || DEFAULT_VOLUME_UNIT}
                        onValueChange={(value) =>
                          handleInputChange('volumeUnit', value as VolumeUnit)
                        }
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getVolumeUnitKeys().map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {VOLUME_UNITS[unit].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {formData.isPackage && (
                    <div className="space-y-2">
                      <Label htmlFor="itemCount">
                        {translate(
                          'pos.inventory.fields.itemCount',
                          'Items per Package'
                        )}
                      </Label>
                      <Input
                        id="itemCount"
                        type="number"
                        min="1"
                        placeholder={translate(
                          'pos.inventory.fields.itemCountPlaceholder',
                          'e.g., 12'
                        )}
                        value={formData.itemCount || ''}
                        onChange={(e) =>
                          handleInputChange(
                            'itemCount',
                            e.target.value
                              ? parseInt(e.target.value)
                              : undefined
                          )
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        {translate(
                          'pos.inventory.fields.itemCountHelp',
                          'Optional: Number of individual items contained in each package'
                        )}
                      </p>
                    </div>
                  )}

                  {/* Quantity Anchor Selection - Only show options that are available */}
                  <div className="space-y-2">
                    <Label htmlFor="quantityAnchor">
                      {translate(
                        'pos.inventory.fields.quantityAnchor',
                        'Quantity Calculation Anchor'
                      )}
                    </Label>
                    <Select
                      value={formData.quantityAnchor || 'none'}
                      onValueChange={(value) =>
                        handleInputChange(
                          'quantityAnchor',
                          value === 'none'
                            ? undefined
                            : (value as 'ITEM_COUNT' | 'WEIGHT' | 'VOLUME')
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={translate(
                            'pos.inventory.fields.quantityAnchorPlaceholder',
                            'Select anchor (optional)'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-muted-foreground">
                            {translate(
                              'pos.inventory.fields.noAnchor',
                              'No anchor (manual quantity)'
                            )}
                          </span>
                        </SelectItem>
                        {formData.itemCount && formData.itemCount > 0 && (
                          <SelectItem value="ITEM_COUNT">
                            {translate(
                              'pos.inventory.fields.anchorItemCount',
                              'Item Count'
                            )}
                          </SelectItem>
                        )}
                        {formData.weight && formData.weight > 0 && (
                          <SelectItem value="WEIGHT">
                            {translate(
                              'pos.inventory.fields.anchorWeight',
                              'Weight'
                            )}
                          </SelectItem>
                        )}
                        {formData.volume && formData.volume > 0 && (
                          <SelectItem value="VOLUME">
                            {translate(
                              'pos.inventory.fields.anchorVolume',
                              'Volume'
                            )}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {translate(
                        'pos.inventory.fields.quantityAnchorHelp',
                        'Select which property to use for automatic quantity calculation. When set, quantity will be calculated from remaining values and cannot be edited manually.'
                      )}
                    </p>
                  </div>

                  {/* Remaining Values - shown based on anchor */}
                  {formData.quantityAnchor === 'ITEM_COUNT' &&
                    formData.itemCount &&
                    formData.itemCount > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="remainingItems">
                          {translate(
                            'pos.inventory.fields.remainingItems',
                            'Remaining Items'
                          )}{' '}
                          *
                        </Label>
                        <Input
                          id="remainingItems"
                          type="number"
                          min="0"
                          placeholder="0"
                          value={formData.remainingItems || ''}
                          onChange={(e) => {
                            const remainingItems = e.target.value
                              ? parseInt(e.target.value)
                              : undefined;
                            handleInputChange('remainingItems', remainingItems);
                            // Calculate quantity automatically
                            if (
                              remainingItems !== undefined &&
                              formData.itemCount &&
                              formData.itemCount > 0
                            ) {
                              handleInputChange(
                                'quantity',
                                remainingItems / formData.itemCount
                              );
                            }
                          }}
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          {translate(
                            'pos.inventory.fields.remainingItemsHelp',
                            `Total remaining items. Quantity will be calculated as: remainingItems / ${formData.itemCount}`
                          )}
                        </p>
                      </div>
                    )}

                  {formData.quantityAnchor === 'WEIGHT' &&
                    formData.weight &&
                    formData.weight > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="remainingWeight">
                          {translate(
                            'pos.inventory.fields.remainingWeight',
                            'Remaining Weight'
                          )}{' '}
                          *
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Weight className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="remainingWeight"
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="0.0"
                              value={formData.remainingWeight || ''}
                              onChange={(e) => {
                                const remainingWeight = e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined;
                                handleInputChange(
                                  'remainingWeight',
                                  remainingWeight
                                );
                                // Calculate quantity automatically
                                if (
                                  remainingWeight !== undefined &&
                                  formData.weight &&
                                  formData.weight > 0
                                ) {
                                  handleInputChange(
                                    'quantity',
                                    remainingWeight / formData.weight
                                  );
                                }
                              }}
                              className="pl-10"
                              required
                            />
                          </div>
                          <Select
                            value={formData.weightUnit || DEFAULT_WEIGHT_UNIT}
                            onValueChange={(value) =>
                              handleInputChange(
                                'weightUnit',
                                value as WeightUnit
                              )
                            }
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getWeightUnitKeys().map((unit) => (
                                <SelectItem key={unit} value={unit}>
                                  {WEIGHT_UNITS[unit].label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {translate(
                            'pos.inventory.fields.remainingWeightHelp',
                            `Total remaining weight. Quantity will be calculated as: remainingWeight / ${formData.weight}`
                          )}
                        </p>
                      </div>
                    )}

                  {formData.quantityAnchor === 'VOLUME' &&
                    formData.volume &&
                    formData.volume > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="remainingVolume">
                          {translate(
                            'pos.inventory.fields.remainingVolume',
                            'Remaining Volume'
                          )}{' '}
                          *
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Box className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="remainingVolume"
                              type="number"
                              min="0"
                              step="0.001"
                              placeholder="0.000"
                              value={formData.remainingVolume || ''}
                              onChange={(e) => {
                                const remainingVolume = e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined;
                                handleInputChange(
                                  'remainingVolume',
                                  remainingVolume
                                );
                                // Calculate quantity automatically
                                if (
                                  remainingVolume !== undefined &&
                                  formData.volume &&
                                  formData.volume > 0
                                ) {
                                  handleInputChange(
                                    'quantity',
                                    remainingVolume / formData.volume
                                  );
                                }
                              }}
                              className="pl-10"
                              required
                            />
                          </div>
                          <Select
                            value={formData.volumeUnit || DEFAULT_VOLUME_UNIT}
                            onValueChange={(value) =>
                              handleInputChange(
                                'volumeUnit',
                                value as VolumeUnit
                              )
                            }
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getVolumeUnitKeys().map((unit) => (
                                <SelectItem key={unit} value={unit}>
                                  {VOLUME_UNITS[unit].label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {translate(
                            'pos.inventory.fields.remainingVolumeHelp',
                            `Total remaining volume. Quantity will be calculated as: remainingVolume / ${formData.volume}`
                          )}
                        </p>
                      </div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              {translate('common.cancel', 'Cancel')}
            </Button>
            {inventory && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrintQRCode}
                disabled={loading}
              >
                <Printer className="h-4 w-4 mr-2" />
                {translate('pos.inventory.printQR', 'Print QR Code')}
              </Button>
            )}
            <Button type="submit" disabled={!isFormValid() || loading}>
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {inventory
                ? translate('common.update', 'Update')
                : translate('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Quick Create Ingredient Dialog */}
      <ModernIngredientForm
        open={ingredientFormOpen}
        onClose={() => setIngredientFormOpen(false)}
        onSave={async (ingredient: Ingredient) => {
          // Reload ingredients to include the new one
          await loadIngredients();
          // Automatically select the newly created ingredient
          setFormData((prev) => ({
            ...prev,
            ingredientId: ingredient.id,
            productId: '', // Clear productId when ingredient is selected
          }));
          setIngredientFormOpen(false);
        }}
        ingredient={null}
      />
    </Dialog>
  );
};
